import { useTranslation } from "react-i18next";
import { useAsyncFn, useInterval } from "react-use";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { ArrayHelper } from "@/helpers";
import { useAppConfig, useAuth, useMessage } from "@/contexts";
import { NotificationMode } from "@/domains/app";
import { BalanceRead, MyBalanceService } from "@/domains/wallet";
import {
  UserCategory,
  MyNotification,
  UserRead,
  UserService,
} from "@/domains/user";
import { getMenu } from "./menu";
// components
import { Avatar, Button, Dropdown, Menu } from "antd";
import {
  MutedOutlined,
  SoundFilled,
  SoundOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { BalanceChip, NotificationChip } from "@/components";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

export const ConsoleLayout = () => {
  const notificationAudio = document.querySelector(
    "#notificationAudio"
  ) as HTMLAudioElement;

  const { t } = useTranslation();

  const navigate = useNavigate();

  const location = useLocation();

  const auth = useAuth();

  const { messageContextHolder } = useMessage();

  const { notificationMode, setNotificationMode } = useAppConfig();

  const [openKeys, setOpenKeys] = useState<string[]>([]);

  const [audioPlayCount, setAudioPlayCount] = useState(0);

  const [prevMyNotification, setPrevMyNotification] =
    useState<MyNotification>();

  const [myNotification, setMyNotification] = useState<MyNotification>();

  const [balances, setBalances] = useState<BalanceRead[]>([]);

  const menuItems = useMemo(() => getMenu(auth, t), [auth, t]);

  useEffect(() => {
    const menuItem = menuItems.find((item) => {
      if (item && "children" in item) {
        return item.children?.find((child) => child?.key === location.pathname);
      }
      return false;
    });

    menuItem && setOpenKeys([menuItem.key as string]);
  }, [location.pathname, menuItems]);

  const [notificationState, getMyNotify] = useAsyncFn(async () => {
    const res = await UserService.getMyNotify().run();
    setPrevMyNotification(myNotification);
    setMyNotification(res.data.msg);
  }, [notificationMode, myNotification]);

  const [balancesState, getMyBalances] = useAsyncFn(async () => {
    const res = await MyBalanceService.list().run();
    setBalances(res.data.msg.balances);
  });

  const getUserReportsMemberWithdrawLink = useCallback(() => {
    let result = "";
    switch (auth.user?.category) {
      case UserCategory.OperatorAccount:
      case UserCategory.OperatorSubAccount:
        result = "/operator/reports/member-withdraw";
        break;
      case UserCategory.TenantAccount:
      case UserCategory.TenantSubAccount:
        result = "/tenant/reports/member-withdraw";
        break;
    }
    return result;
  }, [auth.user?.category]);

  const notificationChips = useMemo(() => {
    if (!myNotification) return [];

    const hasMemberWithdrawTransfers =
      myNotification.to_check.member_withdraw_transfers.length > 0;

    const memberWithdrawTransferChip = {
      color: hasMemberWithdrawTransfers ? "red" : "gray",
      title: t("pages.ConsoleLayout.toCheckMemberWithdrawTransfers"),
      route: getUserReportsMemberWithdrawLink(),
      loading: notificationState.loading,
      quantity: myNotification.to_check.member_withdraw_transfers.length ?? 0,
    };

    if (!auth.isOperator) return [memberWithdrawTransferChip];

    const hasMemberDepositTransfers =
      myNotification.to_check.member_deposit_transfers.length > 0;

    const memberDepositTransferChip = {
      color: hasMemberDepositTransfers ? "red" : "gray",
      title: t("pages.ConsoleLayout.toCheckMemberDepositTransfers"),
      route: "/operator/reports/member-deposit",
      loading: notificationState.loading,
      quantity: myNotification.to_check.member_deposit_transfers.length ?? 0,
    };

    const hasTenantWithdrawTransfers =
      myNotification.to_check.tenant_withdraw_transfers.length > 0;

    const tenantWithdrawTransferChip = {
      color: hasTenantWithdrawTransfers ? "red" : "gray",
      title: t("pages.ConsoleLayout.toCheckTenantWithdrawTransfers"),
      route: "/operator/reports/tenant-withdraw",
      loading: notificationState.loading,
      quantity: myNotification.to_check.tenant_withdraw_transfers.length ?? 0,
    };

    const hasTenantDepositTransfers =
      myNotification.to_check.tenant_deposit_transfers.length > 0;

    const tenantDepositTransferChip = {
      color: hasTenantDepositTransfers ? "red" : "gray",
      title: t("pages.ConsoleLayout.toCheckTenantDepositTransfers"),
      route: "/operator/reports/tenant-deposit",
      loading: notificationState.loading,
      quantity: myNotification.to_check.tenant_deposit_transfers.length ?? 0,
    };

    return [
      memberWithdrawTransferChip,
      memberDepositTransferChip,
      tenantWithdrawTransferChip,
      tenantDepositTransferChip,
    ];
  }, [
    t,
    myNotification,
    getUserReportsMemberWithdrawLink,
    notificationState.loading,
    auth.isOperator,
  ]);

  useEffect(() => {
    const playNotificationSound = async () => {
      if (!myNotification) return;

      const transfers = Object.values(myNotification.to_check).flat();
      if (transfers.length === 0) return;

      // 永不提醒模式：不播放提示音
      if (notificationMode === NotificationMode.Never) return;

      // 單次提醒模式：檢查是否出現不同的 transferId
      if (
        prevMyNotification &&
        audioPlayCount > 0 && // 因為 chrome 規定 user 必須互動後才能 autoplay https://developer.chrome.com/blog/autoplay/，所以另外記錄成功撥放次數
        notificationMode === NotificationMode.Once
      ) {
        const keys = Object.keys(
          prevMyNotification.to_check
        ) as (keyof MyNotification["to_check"])[];

        const isMatchedAll = keys.every((key) =>
          ArrayHelper.shallowMatch(
            prevMyNotification.to_check[key],
            myNotification.to_check[key]
          )
        );

        if (isMatchedAll) return;
      }

      try {
        await notificationAudio.play();
        notificationAudio.muted = false;
        setAudioPlayCount((prev) => prev + 1);
      } catch (e) {
        console.log(e);
      }
    };

    void playNotificationSound();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myNotification, prevMyNotification]);

  useEffect(() => {
    void getMyNotify();
    void getMyBalances();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loopGetMyNotify = async () => {
    if (import.meta.env.VITE_DISABLE_LONG_POLLING === "true") return;
    await getMyNotify();
  };

  useInterval(loopGetMyNotify, 10000);

  const loopGetMyBalances = async () => {
    if (import.meta.env.VITE_DISABLE_LONG_POLLING === "true") return;
    await getMyBalances();
  };

  useInterval(loopGetMyBalances, 30000);

  const handleMenuClick = ({ key }: { key: string }) => {
    key && navigate(key);
  };

  const handleLogout = () => {
    auth.logout();
    navigate("/login");
  };

  const getColorByUserCategory = (user: UserRead | undefined) => {
    if (!user) {
      return "";
    }
    switch (user.category / 10) {
      case 1: {
        return "bg:orange-54";
      }
      case 2: {
        return user.category % 2 == 0 ? "bg:crimson-58" : "bg:indigo-56";
      }
      case 3: {
        return "bg:blue-54";
      }
    }
  };

  return (
    <div className="bg:#efefef">
      {messageContextHolder}

      <aside className="fixed top:0 left:0 w:200 h:100vh d:flex flex:col">
        <a
          href="/"
          target="_blank"
          className="px:16 min-h:60 flex ai:center gap:12 bg:#001529"
        >
          <img
            alt="logo"
            className="w:36"
            src={import.meta.env.VITE_APP_LOGO}
          />
          <h2 className="fg:white">{import.meta.env.VITE_APP_NAME}</h2>
        </a>

        <div className="min-h:0 flex:1 bg:#001529">
          <Menu
            theme="dark"
            mode="inline"
            className="h:full overflow-y:auto"
            items={menuItems}
            openKeys={openKeys}
            selectedKeys={[location.pathname]}
            defaultSelectedKeys={["/"]}
            onClick={handleMenuClick}
            onOpenChange={(keys) => setOpenKeys(keys)}
          />
        </div>
      </aside>

      <div className="h:100vh ml:200 pt:60 flex flex:col">
        <header className="z:10 fixed top:0 w:calc(100vw-200) h:60 px:16 flex ai:center gap:16 bg:white shadow:0|2|8|black/.2">
          <div className="flex:1"></div>

          <Dropdown
            menu={{
              selectable: true,
              defaultSelectedKeys: [notificationMode],
              items: [
                {
                  label: t("systems.NotificationMode.always"),
                  key: NotificationMode.Always,
                  onClick: () => setNotificationMode(NotificationMode.Always),
                },
                {
                  label: t("systems.NotificationMode.once"),
                  key: NotificationMode.Once,
                  onClick: () => {
                    setNotificationMode(NotificationMode.Once);
                    setAudioPlayCount(0); // 重置播放次數
                  },
                },
                {
                  label: t("systems.NotificationMode.never"),
                  key: NotificationMode.Never,
                  onClick: () => setNotificationMode(NotificationMode.Never),
                },
              ],
            }}
            trigger={["click"]}
            arrow={{ pointAtCenter: true }}
          >
            <div className="cursor:pointer">
              {notificationMode === NotificationMode.Always && (
                <SoundFilled style={{ color: "red" }} />
              )}
              {notificationMode === NotificationMode.Once && (
                <SoundOutlined style={{ color: "red" }} />
              )}
              {notificationMode === NotificationMode.Never && (
                <MutedOutlined style={{ color: "gray" }} className="mr:2" />
              )}
            </div>
          </Dropdown>

          {notificationChips.map((props) => (
            <NotificationChip {...props} key={props.route} />
          ))}

          {balances.map((balance, idx) => (
            <BalanceChip
              {...balance}
              loading={balancesState.loading}
              key={idx}
            />
          ))}

          <div className="flex ai:center gap:6">
            <Avatar
              className={getColorByUserCategory(auth.user)}
              icon={<UserOutlined />}
            />
            {auth.user?.name ? (
              <div className="mt:2">
                {auth.user?.name} ({auth.user?.username})
              </div>
            ) : (
              <div className="mt:2">{auth.user?.username}</div>
            )}
          </div>

          <LanguageSwitcher />

          <Button danger type="primary" onClick={handleLogout}>
            {t("pages.ConsoleLayout.logout")}
          </Button>
        </header>

        <main className="p:16 flex:1 overflow-y:auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
